/**
 * ContainersPanel - Inventario de contenedores del proyecto
 *
 * @version 1.0
 * @updated 2026-02-22
 * @module core/modules/waste/components/panels
 *
 * @description
 * Tabla de contenedores del proyecto activo, siguiendo el patrón de TreesPanel.
 * Dos modos de visualización:
 * - Narrow (collapsed=false): checkbox + eye + lupa + código + tipo
 * - Wide (collapsed=true): checkbox + eye + lupa + código + tipo + estado + llenado + zona
 *
 * @remarks
 * - Datos de solo lectura (editables únicamente en el panel derecho).
 * - Eye: toggle de visibilidad individual en mapa.
 * - Checkbox: multi-selección para acciones masivas.
 * - Hover bidireccional tabla ↔ mapa via custom events.
 * - Paginación dinámica con ResizeObserver.
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Eye, EyeOff, Search, Trash2,
  AlertCircle, Loader2,
  ChevronUp, ChevronDown,
  Link2,
} from 'lucide-react';
import { useProject } from '../../../../projects';
import { useLeftPanel } from '../../../../layouts/panels/LeftPanel/LeftPanel';
import { TablePagination } from '../../../../layouts/panels/LeftPanel/TablePagination';
import { ConfirmDialog } from '../../../../components';
import { wasteApi } from '../../services';
import { CONTAINER_TYPES, CONTAINER_STATUSES, getFillLevelColor, getFillLevelLabel } from '../../constants';
import type { WasteContainer } from '../../types';
import { generateGeometryFromContainers } from '../../utils/route-geometry-utils';
import './containers-panel.css';

// ─────────────────────────────────────────
// Constants
// ─────────────────────────────────────────

/** Altura aproximada de cada fila en px — sincronizar con CSS min-height. */
const ROW_HEIGHT_PX = 26;

// ─────────────────────────────────────────
// Types
// ─────────────────────────────────────────

export interface ContainerRow {
  id: string;
  code: string | null;
  containerType: string;
  status: string;
  fillLevel: number | null;
  zoneName: string | null;
  address: string | null;
  coordinates: [number, number];
}

type SortDir = 'asc' | 'desc';

// ─────────────────────────────────────────
// Column definitions (wide mode)
// ─────────────────────────────────────────

const WIDE_COLUMNS = [
  { key: 'code', label: 'Cód', title: 'Código del contenedor' },
  { key: 'containerType', label: 'Tipo', title: 'Tipo de residuo' },
  { key: 'status', label: 'Estado', title: 'Estado del contenedor' },
  { key: 'fillLevel', label: 'Llenado', title: 'Nivel de llenado' },
  { key: 'zoneName', label: 'Zona', title: 'Zona de cobertura' },
] as const;

// ─────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────

function buildContainerRow(c: WasteContainer): ContainerRow {
  return {
    id: c.id,
    code: c.code ?? null,
    containerType: c.containerType,
    status: c.status,
    fillLevel: c.currentFill ?? null,
    zoneName: c.zone?.name ?? null,
    address: c.metadata?.address ?? null,
    coordinates: c.geometry.coordinates as [number, number],
  };
}

function translateContainerType(v: string): string {
  const type = CONTAINER_TYPES.find(t => t.value === v);
  return type?.label ?? v;
}

function translateStatus(v: string): string {
  const status = CONTAINER_STATUSES.find(s => s.value === v);
  return status?.label ?? v;
}

function formatFillLevel(v: number | null): string {
  if (v === null || v === undefined) return '—';
  return `${Math.round(v)}%`;
}

function getCellValue(row: ContainerRow, key: string): string {
  switch (key) {
    case 'code': return row.code ?? '—';
    case 'containerType': return translateContainerType(row.containerType);
    case 'status': return translateStatus(row.status);
    case 'fillLevel': return formatFillLevel(row.fillLevel);
    case 'zoneName': return row.zoneName ?? '—';
    default: return '—';
  }
}

function openContainerDetails(row: ContainerRow): void {
  window.dispatchEvent(
    new CustomEvent('waste-container-select', {
      detail: {
        containerId: row.id,
        coordinates: row.coordinates,
        source: 'sidebar',
      },
    }),
  );
}

// ─────────────────────────────────────────
// Component
// ─────────────────────────────────────────

/**
 * Panel de inventario de contenedores.
 * Solo lectura — la edición ocurre en el panel derecho (ContainerDetailsPanel).
 */
export function ContainersPanel(): JSX.Element {
  const { projectId } = useProject();
  const { collapsed } = useLeftPanel();

  // ── Data ──────────────────────────────────
  const [rows, setRows] = useState<ContainerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Visibility (eye toggle per element) ───
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());

  // ── Multi-select (checkbox) ───────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // ── Sort ──────────────────────────────────
  const [sortKey, setSortKey] = useState<string>('code');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // ── Pagination ────────────────────────────
  const [page, setPage] = useState(1);
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);
  const bodyRef = useCallback((el: HTMLDivElement | null) => setBodyEl(el), []);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // ── Hover (bidirectional map ↔ table) ─────
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // ── Data fetch ────────────────────────────
  const loadContainers = useCallback(async () => {
    if (!projectId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const data = await wasteApi.containers.getByProject(projectId);
      setRows(data.map(buildContainerRow));
    } catch {
      setError('Error al cargar los contenedores');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { void loadContainers(); }, [loadContainers]);

  // Escuchar eventos para mantener datos actualizados
  useEffect(() => {
    const handleCreated = (): void => { void loadContainers(); };
    const handleUpdated = (e: Event): void => {
      const updated = (e as CustomEvent<WasteContainer>).detail;
      setRows(prev => prev.map(r => r.id === updated.id ? buildContainerRow(updated) : r));
    };
    const handleDeleted = (e: Event): void => {
      const { containerId } = (e as CustomEvent<{ containerId: string }>).detail;
      setRows(prev => prev.filter(r => r.id !== containerId));
    };
    window.addEventListener('container-created', handleCreated);
    window.addEventListener('container-updated', handleUpdated);
    window.addEventListener('container-deleted', handleDeleted);
    return () => {
      window.removeEventListener('container-created', handleCreated);
      window.removeEventListener('container-updated', handleUpdated);
      window.removeEventListener('container-deleted', handleDeleted);
    };
  }, [loadContainers]);

  // ── ResizeObserver: rows per page ─────────
  useEffect(() => {
    if (!bodyEl) return;
    const observer = new ResizeObserver(([entry]) => {
      const h = entry.contentRect.height;
      if (h > 0) setRowsPerPage(Math.max(1, Math.floor(h / ROW_HEIGHT_PX)));
    });
    observer.observe(bodyEl);
    return () => observer.disconnect();
  }, [bodyEl]);

  // ── Sort handler ──────────────────────────
  const handleSort = (key: string): void => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  // ── Sorted rows ───────────────────────────
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const aVal = getCellValue(a, sortKey);
      const bVal = getCellValue(b, sortKey);
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, sortKey, sortDir]);

  // Ajustar página si rowsPerPage cambia
  useEffect(() => {
    const max = Math.ceil(sortedRows.length / rowsPerPage) || 1;
    if (page > max) setPage(max);
  }, [rowsPerPage, sortedRows.length, page]);

  const totalPages = Math.ceil(sortedRows.length / rowsPerPage);

  const pageRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  // ── Visibility toggle (individual) ────────
  const handleToggleVisibility = (id: string): void => {
    setHiddenIds(prev => {
      const next = new Set(prev);
      const willHide = !next.has(id);
      if (willHide) { next.add(id); } else { next.delete(id); }
      window.dispatchEvent(new CustomEvent('container-visibility-toggle', {
        detail: { containerId: id, visible: !willHide },
      }));
      return next;
    });
  };

  // ── Bulk visibility toggle ─────────────────
  const handleBulkToggleVisibility = (): void => {
    const anyVisible = [...selectedIds].some(id => !hiddenIds.has(id));
    setHiddenIds(prev => {
      const next = new Set(prev);
      selectedIds.forEach(id => {
        if (anyVisible) { next.add(id); } else { next.delete(id); }
        window.dispatchEvent(new CustomEvent('container-visibility-toggle', {
          detail: { containerId: id, visible: !anyVisible },
        }));
      });
      return next;
    });
  };

  // ── Checkbox: individual ──────────────────
  const handleToggleSelect = (id: string): void => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  // ── Checkbox: select/deselect all on page ─
  const handleSelectAll = (): void => {
    const allSelected = pageRows.every(r => selectedIds.has(r.id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        pageRows.forEach(r => next.delete(r.id));
      } else {
        pageRows.forEach(r => next.add(r.id));
      }
      return next;
    });
  };

  // ── Zoom to container in map ───────────────
  const handleZoom = (row: ContainerRow): void => {
    window.dispatchEvent(new CustomEvent('container-zoom', {
      detail: { coordinates: row.coordinates },
    }));
  };

  // ── Bulk delete ───────────────────────────
  const handleBulkDelete = async (): Promise<void> => {
    const count = selectedIds.size;
    if (count === 0) return;

    const confirmed = await ConfirmDialog.show({
      title: `¿Eliminar ${count} contenedor${count === 1 ? '' : 'es'}?`,
      message: `Se eliminarán ${count} contenedor${count === 1 ? '' : 'es'} permanentemente.\n\nEsta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      type: 'danger',
    });
    if (!confirmed) return;

    const toDelete = [...selectedIds];
    const results = await Promise.allSettled(toDelete.map(id => wasteApi.containers.delete(id)));

    const deleted = toDelete.filter((_, i) => results[i].status === 'fulfilled');
    const failed = toDelete.filter((_, i) => results[i].status === 'rejected');

    if (deleted.length > 0) {
      const deletedSet = new Set(deleted);
      setRows(prev => prev.filter(r => !deletedSet.has(r.id)));
      setSelectedIds(prev => { const next = new Set(prev); deleted.forEach(id => next.delete(id)); return next; });
      deleted.forEach(id =>
        window.dispatchEvent(new CustomEvent('container-deleted', { detail: { containerId: id } }))
      );
    }
    if (failed.length > 0) {
      console.error('[ContainersPanel] No se pudieron eliminar:', failed);
      setError(`No se ${failed.length === 1 ? 'pudo eliminar 1 contenedor' : `pudieron eliminar ${failed.length} contenedores`}.`);
    }
  };

  // ── Connect selected containers ───────────
  const handleConnectContainers = async (): Promise<void> => {
    const count = selectedIds.size;
    if (count < 2) return;

    try {
      console.log('[ContainersPanel] Conectando contenedores:', selectedIds);

      // Obtener contenedores seleccionados manteniendo el orden
      const selectedContainerIds = [...selectedIds];
      const containers: WasteContainer[] = [];

      // Cargar datos completos de los contenedores
      for (const id of selectedContainerIds) {
        try {
          const container = await wasteApi.containers.getById(id);
          containers.push(container);
        } catch (err) {
          console.error('[ContainersPanel] Error cargando contenedor:', id, err);
        }
      }

      if (containers.length < 2) {
        setError('No se pudieron cargar suficientes contenedores para crear la ruta');
        return;
      }

      // Generar geometría desde contenedores
      const geometry = generateGeometryFromContainers(containers);

      console.log('[ContainersPanel] Geometría generada:', geometry);

      // Despachar evento para abrir CreateRoutePanel con datos pre-cargados
      window.dispatchEvent(
        new CustomEvent('waste-open-create-route-panel', {
          detail: {
            geometry,
            containerIds: containers.map((c) => c.id),
          },
        })
      );

      // Limpiar selección
      setSelectedIds(new Set());
    } catch (err) {
      console.error('[ContainersPanel] Error conectando contenedores:', err);
      setError('Error al generar la ruta desde los contenedores seleccionados');
    }
  };

  // ── Sort icon helper ──────────────────────
  const sortIcon = (key: string): React.ReactNode => {
    if (sortKey !== key) return null;
    return sortDir === 'asc'
      ? <ChevronUp size={9} className="cp-sort-icon" />
      : <ChevronDown size={9} className="cp-sort-icon" />;
  };

  // ── Derived state ─────────────────────────
  const hasSelection = selectedIds.size > 0;
  const allOnPageSelected = pageRows.length > 0 && pageRows.every(r => selectedIds.has(r.id));
  const someOnPageSelected = pageRows.some(r => selectedIds.has(r.id));
  const anySelectedVisible = hasSelection && [...selectedIds].some(id => !hiddenIds.has(id));

  // ── Early return: no project ──────────────
  if (!projectId) {
    return (
      <div className="cp-state">
        <AlertCircle size={20} />
        <p>No hay proyecto activo</p>
      </div>
    );
  }

  // ── Render ────────────────────────────────
  return (
    <section className="cp-section" aria-label="Inventario de contenedores">

      {/* ── Section header ── */}
      <header className={`cp-section-header${hasSelection ? ' has-selection' : ''}`}>
        {hasSelection ? (
          <>
            <span className="cp-selected-count">{selectedIds.size} seleccionados</span>
            <div className="cp-bulk-actions">
              <button
                className={`cp-bulk-btn${anySelectedVisible ? '' : ' all-hidden'}`}
                onClick={handleBulkToggleVisibility}
                title={anySelectedVisible ? 'Ocultar en mapa' : 'Mostrar en mapa'}
                aria-label={anySelectedVisible ? 'Ocultar seleccionados en mapa' : 'Mostrar seleccionados en mapa'}
              >
                {anySelectedVisible ? <Eye size={12} /> : <EyeOff size={12} />}
              </button>
              {selectedIds.size >= 2 && (
                <button
                  className="cp-bulk-btn cp-bulk-btn--connect"
                  onClick={() => void handleConnectContainers()}
                  title="Crear ruta conectando contenedores seleccionados"
                  aria-label="Conectar contenedores seleccionados"
                >
                  <Link2 size={12} />
                </button>
              )}
              <button
                className="cp-bulk-btn cp-bulk-btn--danger"
                onClick={() => void handleBulkDelete()}
                title="Eliminar seleccionados"
                aria-label="Eliminar contenedores seleccionados"
              >
                <Trash2 size={12} />
              </button>
            </div>
          </>
        ) : (
          <>
            <span className="cp-section-title">Contenedores</span>
            <span className="cp-section-count">{rows.length.toLocaleString()}</span>
          </>
        )}
      </header>

      {/* ── Loading ── */}
      {loading && (
        <div className="cp-state">
          <Loader2 size={16} className="cp-spinner" />
          <p>Cargando…</p>
        </div>
      )}

      {/* ── Error ── */}
      {error && !loading && (
        <div className="cp-state cp-state--error">
          <AlertCircle size={16} />
          <p>{error}</p>
          <button className="cp-retry-btn" onClick={() => void loadContainers()}>Reintentar</button>
        </div>
      )}

      {/* ── Empty ── */}
      {!loading && !error && rows.length === 0 && (
        <div className="cp-state cp-state--empty">
          <p>No hay contenedores registrados</p>
        </div>
      )}

      {/* ── Table ── */}
      {!loading && !error && rows.length > 0 && (
        <>
          {/* ════ Narrow mode ════ */}
          {!collapsed && (
            <>
              <div className="cp-header cp-header--narrow">
                <input
                  type="checkbox"
                  className="cp-checkbox"
                  checked={allOnPageSelected}
                  ref={el => {
                    if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected;
                  }}
                  onChange={handleSelectAll}
                  title="Seleccionar todos en la página"
                  aria-label="Seleccionar todos"
                />
                <span className="cp-eye-placeholder" />
                <span className="cp-zoom-placeholder" />
                <button
                  className={`cp-col-header${sortKey === 'code' ? ' active' : ''}`}
                  onClick={() => handleSort('code')}
                  title="Ordenar por código"
                >
                  Código {sortIcon('code')}
                </button>
                <button
                  className={`cp-col-header cp-col-header--flex${sortKey === 'containerType' ? ' active' : ''}`}
                  onClick={() => handleSort('containerType')}
                  title="Ordenar por tipo"
                >
                  Tipo {sortIcon('containerType')}
                </button>
              </div>

              <div className="cp-body" ref={bodyRef}>
                {pageRows.map(row => {
                  const isHidden = hiddenIds.has(row.id);
                  const isSelected = selectedIds.has(row.id);
                  const isHovered = hoveredId === row.id;
                  const typeColor = CONTAINER_TYPES.find(t => t.value === row.containerType)?.color;
                  return (
                    <div
                      key={row.id}
                      className={[
                        'cp-row cp-row--narrow',
                        isHidden ? 'hidden' : '',
                        isHovered ? 'hovered' : '',
                        isSelected ? 'selected' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => openContainerDetails(row)}
                      onMouseEnter={() => {
                        setHoveredId(row.id);
                        window.dispatchEvent(new CustomEvent('container-hover', { detail: { containerId: row.id } }));
                      }}
                      onMouseLeave={() => {
                        setHoveredId(null);
                        window.dispatchEvent(new CustomEvent('container-hover', { detail: { containerId: null } }));
                      }}
                    >
                      <input
                        type="checkbox"
                        className="cp-checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelect(row.id)}
                        onClick={e => e.stopPropagation()}
                        aria-label={`Seleccionar contenedor ${row.code ?? row.id}`}
                      />
                      <button
                        className={`cp-eye-btn${isHidden ? ' hidden' : ''}`}
                        onClick={e => { e.stopPropagation(); handleToggleVisibility(row.id); }}
                        title={isHidden ? 'Mostrar en mapa' : 'Ocultar en mapa'}
                        aria-label={isHidden ? 'Mostrar en mapa' : 'Ocultar en mapa'}
                      >
                        {isHidden ? <EyeOff size={9} /> : <Eye size={9} />}
                      </button>
                      <button
                        className="cp-zoom-btn"
                        onClick={e => { e.stopPropagation(); handleZoom(row); }}
                        title="Acercarse en el mapa"
                        aria-label="Acercarse"
                      >
                        <Search size={9} />
                      </button>
                      <span className="cp-cell cp-cell--code" title={row.code ?? undefined}>
                        {row.code ?? '—'}
                      </span>
                      <span
                        className="cp-cell cp-cell--type"
                        title={translateContainerType(row.containerType)}
                        style={{ '--type-color': typeColor } as React.CSSProperties}
                      >
                        <span className="cp-type-dot" />
                        {translateContainerType(row.containerType)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ════ Wide mode ════ */}
          {collapsed && (
            <>
              <div className="cp-header cp-header--wide">
                <input
                  type="checkbox"
                  className="cp-checkbox"
                  checked={allOnPageSelected}
                  ref={el => {
                    if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected;
                  }}
                  onChange={handleSelectAll}
                  title="Seleccionar todos en la página"
                  aria-label="Seleccionar todos"
                />
                <span className="cp-eye-placeholder" />
                <span className="cp-zoom-placeholder" />
                {WIDE_COLUMNS.map(col => (
                  <button
                    key={col.key}
                    className={`cp-col-header${sortKey === col.key ? ' active' : ''}`}
                    onClick={() => handleSort(col.key)}
                    title={`${col.title} — clic para ordenar`}
                  >
                    {col.label} {sortIcon(col.key)}
                  </button>
                ))}
              </div>

              <div className="cp-body" ref={bodyRef}>
                {pageRows.map(row => {
                  const isHidden = hiddenIds.has(row.id);
                  const isSelected = selectedIds.has(row.id);
                  const isHovered = hoveredId === row.id;
                  const typeColor = CONTAINER_TYPES.find(t => t.value === row.containerType)?.color;
                  const fillColor = row.fillLevel !== null ? getFillLevelColor(row.fillLevel) : undefined;
                  return (
                    <div
                      key={row.id}
                      className={[
                        'cp-row cp-row--wide',
                        isHidden ? 'hidden' : '',
                        isHovered ? 'hovered' : '',
                        isSelected ? 'selected' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => openContainerDetails(row)}
                      onMouseEnter={() => {
                        setHoveredId(row.id);
                        window.dispatchEvent(new CustomEvent('container-hover', { detail: { containerId: row.id } }));
                      }}
                      onMouseLeave={() => {
                        setHoveredId(null);
                        window.dispatchEvent(new CustomEvent('container-hover', { detail: { containerId: null } }));
                      }}
                    >
                      <input
                        type="checkbox"
                        className="cp-checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelect(row.id)}
                        onClick={e => e.stopPropagation()}
                        aria-label={`Seleccionar contenedor ${row.code ?? row.id}`}
                      />
                      <button
                        className={`cp-eye-btn${isHidden ? ' hidden' : ''}`}
                        onClick={e => { e.stopPropagation(); handleToggleVisibility(row.id); }}
                        title={isHidden ? 'Mostrar en mapa' : 'Ocultar en mapa'}
                      >
                        {isHidden ? <EyeOff size={9} /> : <Eye size={9} />}
                      </button>
                      <button
                        className="cp-zoom-btn"
                        onClick={e => { e.stopPropagation(); handleZoom(row); }}
                        title="Acercarse en el mapa"
                      >
                        <Search size={9} />
                      </button>
                      <span className="cp-cell cp-cell--code" title={row.code ?? undefined}>
                        {row.code ?? '—'}
                      </span>
                      <span
                        className="cp-cell cp-cell--type"
                        title={translateContainerType(row.containerType)}
                        style={{ '--type-color': typeColor } as React.CSSProperties}
                      >
                        <span className="cp-type-dot" />
                        {translateContainerType(row.containerType)}
                      </span>
                      <span className="cp-cell cp-cell--status" title={translateStatus(row.status)}>
                        {translateStatus(row.status)}
                      </span>
                      <span
                        className="cp-cell cp-cell--fill"
                        title={row.fillLevel !== null ? `${getFillLevelLabel(row.fillLevel)} (${row.fillLevel}%)` : undefined}
                        style={{ '--fill-color': fillColor } as React.CSSProperties}
                      >
                        {row.fillLevel !== null && <span className="cp-fill-bar" style={{ width: `${row.fillLevel}%` }} />}
                        {formatFillLevel(row.fillLevel)}
                      </span>
                      <span className="cp-cell cp-cell--zone" title={row.zoneName ?? undefined}>
                        {row.zoneName ?? '—'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <TablePagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </section>
  );
}
